import { Button, removeAllToasts, Text, TextField } from '@medly-components/core';
import { useCallback, useEffect, useRef, useState } from 'react';

import { FormInputContainer } from 'components/common/OnBoardingFormWrapper/OnboardingFormWrapper.styled';
import darkBlue from 'theme/colors/darkBlue';
import { errorToast } from 'utils/utils';
import { Password } from '../../components/common/Password/Password';
import Styled from '../../components/common/Styled';
import { DO_NOT_YOU_HAVE_ACCOUNT, EMAIL_ADDRESS, FORGOT_PASSWORD_QUESTION, LOGIN, SIGN_UP } from '../../constants/onboarding';
import { RESET_PASSWORD_ROUTE, SIGN_UP_ROUTE } from '../../constants/routes';
import { emailValidation } from '../../utils/validations';

/**
 * LoginForm Component Props
 * @typedef {object} Props
 * @property {function} clearError - Callback function to clear the error messages displayed on the form.
 * @property {string} error - An array of error messages to be displayed on the form.
 * @property {boolean} loading - A boolean value indicating whether the form is in a loading state.
 * @property {function(IRegistrationBody, string): void} onSubmit - Callback function to handle form submission.
 */
interface Props {
    clearError: () => void;
    error: string;
    loading: boolean;
    onSubmit: (email: string, password: string) => void;
}

type LoginData = {
    email: string;
    password: string;
    emailError: string;
};

const InitialLoginData: LoginData = {
    email: '',
    password: '',
    emailError: ''
};

/**
 * LoginForm Component
 * A reusable component for user login form, including email and password fields.
 * The form handles validation, error display, and form submission via the onSubmit callback.
 */
export const LoginForm = ({ clearError, error = '', loading, onSubmit }: Props) => {
    const [data, setData] = useState<LoginData>(InitialLoginData);
    const formRef = useRef<HTMLFormElement>(null);
    const { email, password, emailError } = data;

    //On email change keep the value in state and clear any error.
    const onEmailChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            clearError();
            setData(prev => ({
                ...prev,
                email: event.target.value,
                emailError: ''
            }));
        },
        [setData, clearError]
    );

    //On email blur validate email.
    const onEmailBlur = useCallback(() => {
        const error = emailValidation(email);
        setData(prev => ({
            ...prev,
            emailError: error
        }));
    }, [emailValidation, setData, email]);

    //On password change keep the value in state and clear any error.
    const onPasswordChangeHandler = useCallback(
        (password: string) => {
            clearError();
            setData(prev => ({
                ...prev,
                password
            }));
        },
        [setData, clearError]
    );

    //On submit call onSubmit callback with email and password
    const submitHandler = useCallback(() => {
        onSubmit(email, password);
    }, [onSubmit, email, password]);

    const disableSubmitButton = emailValidation(email) !== '' || password === '' || loading;
    useEffect(() => {
        errorToast(error);
        return () => {
            removeAllToasts();
        };
    }, [error]);

    const resetForm = () => {
        formRef.current?.reset();
        setData(InitialLoginData);
    };

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key.toLocaleLowerCase() === 'enter' && !disableSubmitButton) {
                onSubmit(data.email, data.password);
            }
        },
        [data]
    );

    useEffect(() => {
        return () => {
            resetForm();
        };
    }, []);

    return (
        <>
            <form ref={formRef}>
                <FormInputContainer direction="column" rowGap="2.4rem">
                    <TextField
                        fullWidth
                        id="email"
                        required
                        label={EMAIL_ADDRESS}
                        onChange={onEmailChange}
                        onKeyDown={onKeyDown}
                        size="M"
                        type="text"
                        value={email}
                        onBlur={onEmailBlur}
                        variant="outlined"
                        errorText={emailError}
                    />
                    <Styled.FlexBox direction="column" rowGap="1.6rem">
                        <Password onKeyDown={onKeyDown} value={password} hasValidator={false} onChange={onPasswordChangeHandler} />
                        <Styled.NoBgBox style={{ marginTop: '0', padding: 0, width: 'fit-content' }}>
                            <Text textVariant="body1" textWeight="Medium">
                                <Styled.TextRouteLink onClick={resetForm} to={RESET_PASSWORD_ROUTE}>
                                    {FORGOT_PASSWORD_QUESTION}
                                </Styled.TextRouteLink>
                            </Text>
                        </Styled.NoBgBox>
                    </Styled.FlexBox>
                </FormInputContainer>
            </form>

            <Styled.FlexBox direction="column" rowGap="1.2rem">
                <Styled.NoBgBox px={0} py={0}>
                    <Button
                        style={{ padding: '1.3rem 0rem' }}
                        isLoading={loading}
                        disabled={disableSubmitButton}
                        edges="square"
                        fullWidth
                        size="M"
                        id="login-btn"
                        type="button"
                        variant="solid"
                        onClick={submitHandler}
                    >
                        {LOGIN}
                    </Button>
                </Styled.NoBgBox>
            </Styled.FlexBox>
            <Styled.FlexBox>
                <Text textAlign="initial" textVariant="body1" textWeight="Medium" textColor={darkBlue} uppercase={false}>
                    {DO_NOT_YOU_HAVE_ACCOUNT}
                </Text>
                <Text style={{ marginLeft: 8 }} textVariant="body1" textWeight="Medium">
                    <Styled.TextRouteAnchor onClick={resetForm} href={SIGN_UP_ROUTE + window.location.search}>
                        {SIGN_UP}
                    </Styled.TextRouteAnchor>
                </Text>
            </Styled.FlexBox>
        </>
    );
};
